import { RefObject } from 'react';
import { TooltipProps } from 'ui/tooltip/types';

import { DEFAULT_CATEGORY_ID } from './constants';
import { BaseOption, CategoryDetail, MultiSelectProps, SelectValueType } from './types';

/**
  When a user clicks out of the basic/multi select container, close the dropdown.
 */
export const clickAwayHandler = (toggleDropdown: (open?: boolean) => void) => {
  toggleDropdown(false);
};

type OptionsAndDefaultsTuple<TValue extends SelectValueType = string> = [
  BaseOption<TValue>[],
  TValue[],
];

// do some string concatentation to build up more durably unique id values
export function createUniqueOptionsAndDefaults<TValue extends SelectValueType = string>(
  id: string,
  options: BaseOption<TValue>[] = [],
  defaultValues: TValue | TValue[] = [],
): OptionsAndDefaultsTuple<TValue> {
  options.forEach((option) => {
    // Please don't go back to object spread syntax or Object.assign!
    // Direct mutation here is both safe and a massive performance improvement.

    option.categoryId = option.categoryId || DEFAULT_CATEGORY_ID; // not all options will have categories

    option.uniqueId = `${id}-${option.id}`;
  });

  const defaultsAsArray = Array.isArray(defaultValues) ? defaultValues : [defaultValues];

  return [options, defaultsAsArray.filter((d) => d != null && `${d}` !== '').sort()];
}

/**
  A partially applied fn for creating dropdown toggles in the selects
 */
export const createDropdownToggle =
  (openFn: (isOpen: boolean) => void, isOpen: boolean, trackToggle?: () => void) =>
  (newValue?: boolean): void => {
    if (typeof newValue === 'boolean') {
      openFn(newValue);
    } else {
      openFn(!isOpen);
    }
    trackToggle?.();
  };

export const focusHandlerForOptionsList = (optionsElement: RefObject<HTMLElement>) => {
  const focusableItems = optionsElement
    ? Array.from(
        optionsElement?.current?.querySelectorAll('input[type="text"], [role="option"]') || [],
      ).map((child) => ({
        el: child as HTMLElement,
        id: (child as HTMLElement).getAttribute('id'),
        isFocused: child === document.activeElement,
      }))
    : [];

  const isItemFocused =
    Boolean(document.activeElement) &&
    focusableItems.map((item) => item.el).includes(document.activeElement as HTMLElement);
  const focusedOptionIndex = focusableItems.findIndex((item) => item.isFocused);
  const isFirstItemFocused = focusedOptionIndex === 0;
  const isLastItemFocused = focusedOptionIndex === focusableItems.length - 1;

  function focusItem(item: any) {
    item.el.focus();
  }
  function focusFirstItem() {
    focusItem(focusableItems[0]);
  }
  function focusLastItem() {
    focusItem(focusableItems[focusableItems.length - 1]);
  }

  function focusPreviousItem() {
    if (isItemFocused && !isFirstItemFocused) {
      focusItem(focusableItems[focusedOptionIndex - 1]);
    } else {
      focusLastItem();
    }
  }
  function focusNextItem() {
    if (!isItemFocused || isLastItemFocused) {
      focusFirstItem();
    } else {
      focusItem(focusableItems[focusedOptionIndex + 1]);
    }
  }

  return {
    focusFirstItem,
    focusLastItem,
    focusPreviousItem,
    focusNextItem,
    isFirstItemFocused,
    isLastItemFocused,
    isItemFocused,
  };
};

export const getFlattenedOptions = <TValue extends SelectValueType = string>(
  getOptions: (category?: string, filter?: string) => BaseOption<TValue>[],
  categoryDetail: Record<string, CategoryDetail>,
  filterText?: string,
) => {
  return Object.values(categoryDetail)
    .map(({ categoryId }) => getOptions(categoryId, filterText))
    .flat() as BaseOption<TValue>[];
};

const createTooltipContent = (
  maxIsReachedText: string,
  tooltipContent: TooltipProps['tooltipContent'],
) => (
  <div>
    <p>{maxIsReachedText}</p>
    {tooltipContent && <p>{tooltipContent}</p>}
  </div>
);

/** Returns props to override disabled and tooltip content when max selected options is reached */
export const getMaxReachedProps = <TValue extends SelectValueType = string>(
  option: BaseOption<TValue>,
  content: MultiSelectProps['content'],
) => {
  const { maxIsReachedTooltipContent = '' } = content || {};
  // Check if option should be disabled
  const shouldDisableOption = !option.isSelected;
  // Combine tooltipContent if provided
  const tooltipContent =
    shouldDisableOption && maxIsReachedTooltipContent
      ? createTooltipContent(maxIsReachedTooltipContent, option.tooltipContent)
      : option.tooltipContent;

  return { isDisabled: shouldDisableOption, 'aria-disabled': shouldDisableOption, tooltipContent };
};

/**
 * Transforms an array of strings into an array of BaseOption objects.
 * @param {string[]} items - The array of strings to transform.
 * @returns {BaseOption[]} - The array of BaseOption objects.
 */
export const transformToBaseOptions = (items: string[]): BaseOption[] => {
  return items.map((item, index) => ({
    label: item,
    value: item,
    id: `${item}-${index}`,
  }));
};
